import React, { useCallback, useMemo } from "react";
import * as Scrivito from "scrivito";
import { ModalHeader } from "reactstrap";
import NiceModal from "@ebay/nice-modal-react";
import { useMutation } from "urql";
import * as Yup from "yup";

import {
  useCountryList,
  useProjectStatuses,
  useTranslate,
  useUserDepartments,
} from "hooks";
import { MAX_PROJECT_TITLE_LENGTH, unitOptions } from "variables/projects";
import { createNodesInput } from "components/ProjectTree/utils";
import { initialNodesStructure } from "variables/initialNodesStructure";
import { useAppBaseContext } from "providers";
import { useAuthStore } from "stores/authStore";

import { useProjectDataStore } from "stores/projectStore";
import { ProjectDetailsForm } from "./ProjectDetailsForm";
import { ModalWrapper } from "../../ModalWrapper";
import { useMandatorOptions } from "./hooks/useMandatorOptions";

export const ProjectDetailsModal = Scrivito.connect(
  NiceModal.create(
    ({ project = {}, mutation, requestParams, resetCallback = () => {} }) => {
      const { selected: preselectedDepartment } = useUserDepartments() ?? {};

      const translate = useTranslate();
      const modal = NiceModal.useModal();
      const projectStatuses = useProjectStatuses();
      const { lang } = useAppBaseContext();
      const { projectMandator } = useProjectDataStore();

      const currentUserTenant = useAuthStore.use.currentUser()?.tenant;
      const countryList = useCountryList({ language: lang });
      const [{ fetching, stale }, execute] = useMutation(mutation);
      const { isSameProj } = useAppBaseContext();

      const translateTree = (node) => {
        const { folderName, nodes, ...rest } = node;
        return {
          ...rest,
          folderName: translate(folderName),
          nodes: nodes?.map(translateTree),
        };
      };
      const { nodesToRequest } = createNodesInput({
        data: initialNodesStructure,
        callback: translateTree,
      });

      const optionsList = useMemo(
        () =>
          projectStatuses?.map((item) => ({
            value: item.status,
            label: translate(`PROJECT_STATUS_${item.status.toUpperCase()}`),
            color: item.color,
          })),
        [projectStatuses, translate],
      );

      const unitOptionsList = unitOptions.map(({ value, label }) => ({
        value,
        label: translate(`PROJECT_UNITS_${label.toUpperCase()}`),
      }));

      const countriesOptions = useMemo(
        () =>
          countryList?.map(({ displayName, isoTwoLetterCode }) => ({
            value: isoTwoLetterCode,
            label: displayName,
          })),
        [countryList],
      );

      const productData =
        requestParams && requestParams?.orderCode
          ? {
              seriesId: requestParams.orderCode.seriesId,
              productName: requestParams.productName,
            }
          : null;

      const mandatorOptions = useMandatorOptions(productData);

      const country = useMemo(() => {
        if (project?.country) {
          return countriesOptions?.find(
            (item) => item.value === project.country,
          );
        }
        return currentUserTenant
          ? countriesOptions?.find(
              (item) => item.value === currentUserTenant?.toLowerCase(),
            )
          : countriesOptions && countriesOptions[0];
      }, [countriesOptions, project.country, currentUserTenant]);

      const initialValues = useMemo(
        () => ({
          title: project.title,
          constructionTitle: project.constructionTitle,
          projectNumber: project.projectNumber,
          description: project.description,
          status: optionsList?.find(
            (item) => item.value === (project?.status?.status || "draft"),
          ),
          country: country || { value: "de", label: "Germany" },
          mandator:
            mandatorOptions.find(
              (item) => item.value === Number(projectMandator),
            ) || mandatorOptions[0],
          units:
            unitOptionsList.find((item) => item.value === project.units) ||
            unitOptionsList[0],
          departmentId: preselectedDepartment,
        }),
        [
          project.title,
          project.constructionTitle,
          project.projectNumber,
          project.description,
          project?.status?.status,
          project.units,
          optionsList,
          country,
          projectMandator,
          mandatorOptions,
          unitOptionsList,
          preselectedDepartment,
        ],
      );

      const validationSchema = useMemo(
        () =>
          Yup.object({
            title: Yup.string()
              .trim(
                "The contact name cannot include leading and trailing spaces",
              )
              .min(4, "PROJECT_NAME_VALIDATION_SHORT")
              .required("PROJECT_NAME_VALIDATION_EMPTY")
              .test("max-length", function validateTitleLength(value) {
                return (
                  value.length <= MAX_PROJECT_TITLE_LENGTH ||
                  this.createError({
                    message: `"PROJECT_NAME_VALIDATION_LONG" ${value.length}`,
                    path: "title",
                    value,
                  })
                );
              }),
            country: Yup.object()
              .typeError(" ")
              .nullable()
              .required(translate("PROJECT_LOCATION_SELECT_VALIDATION_EMPTY")),
            units: Yup.object()
              .typeError(" ")
              .nullable()
              .required(translate("PROJECT_UNITS_VALIDATION_EMPTY")),
          }),
        [translate],
      );

      const handleSubmit = useCallback(
        ({ ...values }) => {
          if (!fetching) {
            const { mandator, ...valuesWithoutMandator } = values;

            const input = project?.id
              ? {
                  id: project.id,
                  languageCode: lang,
                  ...(isSameProj ? valuesWithoutMandator : values),
                  ...(isSameProj ? {} : { mandator: mandator?.value }),
                  status: values.status?.value || projectStatuses?.[0]?.status,
                  country: values.country?.value,
                  units: values.units?.value,
                  departmentId: values.departmentId?.value,
                }
              : {
                  ...values,
                  status: values.status?.value || projectStatuses[0]?.status,
                  country: values.country?.value,
                  units: values.units?.value,
                  languageCode: lang,
                  id: project.id,
                  departmentId: values.departmentId?.value,
                  nodes: nodesToRequest,
                  ...(isSameProj ? { mandator: mandator?.value } : {}),
                };

            execute({ input }).then(({ data, error }) => {
              if (!error) {
                if (!project.id) {
                  modal.resolve(
                    isSameProj ? data.addSameProject : data.addProject,
                  );
                }
                if (project.id) {
                  modal.resolve();
                }
                modal.remove();
              }
            });
            resetCallback();
          }
        },
        [
          project.id,
          fetching,
          execute,
          lang,
          projectStatuses,
          nodesToRequest,
          resetCallback,
          modal,
          isSameProj,
        ],
      );

      return (
        <ModalWrapper
          isOpen={modal.visible}
          centered={true}
          size="xl"
          toggle={modal.hide}
          isLoading={fetching || stale}
        >
          <ModalHeader
            toggle={modal.hide}
            tag="div"
            className="d-flex align-items-start"
          >
            <h3 className="text-uppercase mb-1">
              {translate("PROJECT_PROPERTIES")}
            </h3>
            <h4 className="mb-2_5">
              {translate("PROJECT_MODAL_CREATE_SUB_TITLE")}
            </h4>
            {project.id && (
              <h5 className="mb-0 font16 font-normal gray">
                Project ID: {project.id}
              </h5>
            )}
            {project.owner?.fullName && (
              <p className="mb-0 font16 font-normal gray">
                Project Owner: {project.owner?.fullName}
              </p>
            )}
          </ModalHeader>
          <ProjectDetailsForm
            handleSubmit={handleSubmit}
            initialValues={initialValues}
            isUpdate={!!project.id}
            countriesOptions={countriesOptions}
            mandatorOptions={mandatorOptions}
            optionsList={optionsList}
            projectStatuses={projectStatuses}
            unitOptions={unitOptionsList}
            validationSchema={validationSchema}
            projectExists={!!project?.id}
          />
        </ModalWrapper>
      );
    },
  ),
);
